import React, { useState } from 'react'

import { Button, theme } from '@hhgtech/hhg-components'
import { useIntl } from 'react-intl'

import { formatUserProfileUrl } from 'api/dataTransform'
import { ExpertArticleCardWrapper } from 'components/molecules/expertArticleCardWrapper'
import { domainLocales } from 'domainLocales'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { Article, Author } from 'interfaces/types'

import {
  StyledContainer,
  StyledArticlesContainer,
  StyledArticleContainer,
  StyledSmallArticleContainer,
  StyledArticlesDesktopContainer,
  StyledHeroContainer,
  StyledSubHeroContainer,
  StyledSmallArticleDesktopContainer,
  StyledArticleContainerDesktop,
  StyledHalfScreenDesktopContainer,
} from './index.styled'
import { assetImagePath, DEFAULT_IMG } from '../../../interfaces/constants'
import { StyledAvatarReviewer } from '../expertArticleCardWrapper/index.styled'

export type Props = {
  onArticleClick?: (l: string) => void
  trendingPosts?: Article[]
  isMobile?: boolean
}

const ExpertRelativeArticleCard = ({ isMobile, trendingPosts = [] }: Props) => {
  const { locale } = useRouterExtend()
  const { formatMessage: f } = useIntl()
  const [showingNumber, setShowingNumber] = useState(4)

  const getAuthorUrl = (author: Author) => formatUserProfileUrl(author)

  return (
    <StyledContainer>
      {isMobile ? (
        // mobile
        <StyledArticlesContainer>
          {trendingPosts.slice(0, 1).map((article: Article, id) => (
            <StyledArticleContainer key={article.id} data-size={'custom'}>
              <ExpertArticleCardWrapper
                article={article}
                dataEventCategoryForTitle="Top Article Title"
                dataEventActionForTitle={`Click ${id + 1}`}
                dataEventLabelForTitle={`https://${domainLocales[locale]}${article.permalinkDecode}`}
                dataEventCategoryForBanner="Top Article Banner"
                dataEventActionForBanner={`Click ${id + 1}`}
                dataEventLabelForBanner={`https://${domainLocales[locale]}${article.permalinkDecode}`}
                dataEventCategoryForCategory="Top Category Title"
                dataEventActionForCategory={`Click ${id + 1}`}
                dataEventLabelForCategory={`https://${domainLocales[locale]}${article.category.permalinkDecode}`}
                type="expert"
                size="lg"
                authorName={article?.author?.displayName}
                specialty={article?.author?.specialty}
                banner={
                  <img
                    src={
                      article.featureImageMobile ||
                      article.featureImage ||
                      DEFAULT_IMG
                    }
                    alt={article.postName}
                    loading="lazy"
                  />
                }
                avatarImgRenderer={(avatar, name) => (
                  <StyledAvatarReviewer>
                    <img
                      loading="lazy"
                      src={avatar}
                      className="avatar"
                      alt={name}
                    />
                    {article?.author?.role && (
                      <img
                        loading="lazy"
                        src={`${assetImagePath}/reviewer-${article?.author?.role}.svg`}
                        alt="badge"
                        className="badge"
                        style={{ width: 18, height: 18 }}
                      />
                    )}
                  </StyledAvatarReviewer>
                )}
                categoryLink={article.category.permalink}
                articleLink={article.permalink}
                direction="vertical"
                categoryName={article.category?.displayName}
                title={article.postName}
                text={article.excerpt}
                styleLabel={{
                  color: article.category.textColor,
                }}
                uploadTime={article.modifiedTime}
                authorLink={getAuthorUrl(article?.author)}
              />
            </StyledArticleContainer>
          ))}
          {trendingPosts.slice(1, showingNumber).map((article: Article, id) => (
            <StyledSmallArticleContainer key={article.id}>
              <ExpertArticleCardWrapper
                article={article}
                dataEventCategoryForTitle="Top Article Title"
                dataEventActionForTitle={`Click ${id + 4}`}
                dataEventLabelForTitle={`https://${domainLocales[locale]}${article.permalinkDecode}`}
                dataEventCategoryForBanner="Top Article Banner"
                dataEventActionForBanner={`Click ${id + 4}`}
                dataEventLabelForBanner={`https://${domainLocales[locale]}${article.permalinkDecode}`}
                dataEventActionForCategory="Top Category Title"
                dataEventCategoryForCategory={`Click ${id + 4}`}
                dataEventLabelForCategory={`https://${domainLocales[locale]}${article.category.permalinkDecode}`}
                type="expert"
                size="md"
                authorName={article?.author?.displayName}
                specialty={article?.author?.specialty}
                banner={
                  <img
                    src={
                      article.featureImageMobileXs ||
                      article.featureImage ||
                      DEFAULT_IMG
                    }
                    loading="lazy"
                    style={{ width: '100%', height: '100%' }}
                    alt={article.postName}
                  />
                }
                styleLabel={{
                  color: article.category.textColor,
                }}
                categoryLink={article.category.permalink}
                articleLink={article.permalink}
                categoryName={article.category?.displayName}
                title={article.postName}
                text={article.excerpt}
                avatarImgRenderer={(avatar, name) => (
                  <StyledAvatarReviewer>
                    <img
                      loading="lazy"
                      src={avatar}
                      className="avatar"
                      alt={name}
                    />
                    {article?.author?.role && (
                      <img
                        loading="lazy"
                        src={`${assetImagePath}/reviewer-${article?.author?.role}.svg`}
                        alt="badge"
                        className="badge"
                        style={{ width: 16, height: 16 }}
                      />
                    )}
                  </StyledAvatarReviewer>
                )}
                uploadTime={article.modifiedTime}
                authorLink={getAuthorUrl(article?.author)}
              />
            </StyledSmallArticleContainer>
          ))}
        </StyledArticlesContainer>
      ) : (
        // desktop
        <div
          style={{
            backgroundColor: theme.colors.white,
            width: '100%',
          }}
        >
          <StyledArticlesDesktopContainer>
            <StyledHeroContainer>
              {trendingPosts.slice(0, 1).map((article: Article) => (
                <StyledArticleContainer key={article.id} data-size={'custom'}>
                  <ExpertArticleCardWrapper
                    article={article}
                    dataEventCategoryForTitle="Top Article Title"
                    dataEventActionForTitle="Click 1"
                    dataEventLabelForTitle={`https://${domainLocales[locale]}${article.permalinkDecode}`}
                    dataEventCategoryForCategory="Top Category Title"
                    dataEventActionForCategory="Click 1"
                    dataEventLabelForCategory={`https://${domainLocales[locale]}${article.category.permalinkDecode}`}
                    dataEventCategoryForBanner="Top Article Banner"
                    dataEventActionForBanner="Click 1"
                    dataEventLabelForBanner={`https://${domainLocales[locale]}${article.permalinkDecode}`}
                    type="expert"
                    direction="vertical"
                    size="lg"
                    authorName={article?.author?.displayName}
                    specialty={article?.author?.specialty}
                    banner={
                      <img
                        src={
                          article.featureImageMobile ||
                          article.featureImage ||
                          DEFAULT_IMG
                        }
                        alt={article.postName}
                        key={article.id}
                        style={{ width: '100%', height: '100%' }}
                        loading="lazy"
                      />
                    }
                    styleLabel={{
                      color: article.category.textColor,
                    }}
                    categoryLink={article.category.permalink}
                    articleLink={article.permalink}
                    categoryName={article.category?.displayName}
                    title={article.postName}
                    text={article.excerpt}
                    avatarImg={
                      <StyledAvatarReviewer>
                        <img
                          loading="lazy"
                          src={article?.author?.thumbnailAvatar}
                          className="avatar"
                          alt={f({ id: 'articlePage.authorAvatarAlt' })}
                          style={{ width: 64, height: 64 }}
                        />
                        {article?.author?.role && (
                          <img
                            loading="lazy"
                            src={`${assetImagePath}/reviewer-${article?.author?.role}.svg`}
                            alt="badge"
                            className="badge"
                            style={{ width: 24, height: 24 }}
                          />
                        )}
                      </StyledAvatarReviewer>
                    }
                    uploadTime={article.modifiedTime}
                    authorLink={getAuthorUrl(article?.author)}
                  />
                </StyledArticleContainer>
              ))}
            </StyledHeroContainer>
            <StyledSubHeroContainer>
              {trendingPosts.slice(1, 4).map((article: Article, id) => (
                <StyledSmallArticleDesktopContainer
                  key={article.id}
                  data-size={'custom'}
                >
                  <ExpertArticleCardWrapper
                    article={article}
                    dataEventCategoryForTitle="Top Article Title"
                    dataEventActionForTitle={`Click ${id + 4}`}
                    dataEventLabelForTitle={`https://${domainLocales[locale]}${article.permalinkDecode}`}
                    dataEventCategoryForBanner="Top Article Banner"
                    dataEventActionForBanner={`Click ${id + 4}`}
                    dataEventLabelForBanner={`https://${domainLocales[locale]}${article.permalinkDecode}`}
                    dataEventCategoryForCategory="Top Category Title"
                    dataEventActionForCategory={`Click ${id + 4}`}
                    dataEventLabelForCategory={`https://${domainLocales[locale]}${article.category.permalinkDecode}`}
                    type="expert"
                    size="md"
                    direction="horizontal"
                    authorName={article?.author?.displayName}
                    specialty={article?.author?.specialty}
                    banner={
                      <img
                        loading="lazy"
                        src={
                          article.featureImageMobileXs ||
                          article.featureImage ||
                          DEFAULT_IMG
                        }
                        alt={article.postName}
                        style={{ width: '100%', height: '100%' }}
                      />
                    }
                    categoryLink={article.category.permalink}
                    articleLink={article.permalink}
                    categoryName={article.category?.displayName}
                    title={article.postName}
                    text=""
                    avatarImgRenderer={(avatar, name) => (
                      <StyledAvatarReviewer>
                        <img
                          loading="lazy"
                          src={avatar}
                          className="avatar"
                          alt={name}
                        />
                        {article?.author?.role && (
                          <img
                            loading="lazy"
                            src={`${assetImagePath}/reviewer-${article?.author?.role}.svg`}
                            alt="badge"
                            className="badge"
                            style={{ width: 16, height: 16 }}
                          />
                        )}
                      </StyledAvatarReviewer>
                    )}
                    styleLabel={{
                      color: article.category.textColor,
                    }}
                    uploadTime={article.modifiedTime}
                    authorLink={getAuthorUrl(article?.author)}
                  />
                </StyledSmallArticleDesktopContainer>
              ))}
            </StyledSubHeroContainer>
          </StyledArticlesDesktopContainer>
          <StyledArticleContainerDesktop>
            {trendingPosts
              .slice(4, showingNumber)
              .map((article: Article, id) => (
                <StyledHalfScreenDesktopContainer
                  key={article.id}
                  data-size={'custom'}
                >
                  <ExpertArticleCardWrapper
                    article={article}
                    dataEventCategoryForTitle="Top Article Title"
                    dataEventActionForTitle={`Click ${id + 4}`}
                    dataEventLabelForTitle={`https://${domainLocales[locale]}${article.permalinkDecode}`}
                    dataEventCategoryForBanner="Top Article Banner"
                    dataEventActionForBanner={`Click ${id + 4}`}
                    dataEventLabelForBanner={`https://${domainLocales[locale]}${article.permalinkDecode}`}
                    dataEventCategoryForCategory="Top Category Title"
                    dataEventActionForCategory={`Click ${id + 4}`}
                    dataEventLabelForCategory={`https://${domainLocales[locale]}${article.category.permalinkDecode}`}
                    type="expert"
                    size="md"
                    direction="horizontal"
                    authorName={article?.author?.displayName}
                    specialty={article?.author?.specialty}
                    banner={
                      <img
                        loading="lazy"
                        src={
                          article.featureImageMobileXs ||
                          article.featureImage ||
                          DEFAULT_IMG
                        }
                        alt={article.postName}
                        style={{ width: '100%', height: '100%' }}
                      />
                    }
                    categoryLink={article.category.permalink}
                    articleLink={article.permalink}
                    categoryName={article.category?.displayName}
                    title={article.postName}
                    text=""
                    avatarImgRenderer={(avatar, name) => (
                      <StyledAvatarReviewer>
                        <img
                          loading="lazy"
                          src={avatar}
                          className="avatar"
                          alt={name}
                        />
                        {article?.author?.role && (
                          <img
                            loading="lazy"
                            src={`${assetImagePath}/reviewer-${article?.author?.role}.svg`}
                            alt="badge"
                            className="badge"
                            style={{ width: 16, height: 16 }}
                          />
                        )}
                      </StyledAvatarReviewer>
                    )}
                    styleLabel={{
                      color: article.category.textColor,
                    }}
                    uploadTime={article.modifiedTime}
                    authorLink={getAuthorUrl(article?.author)}
                  />
                </StyledHalfScreenDesktopContainer>
              ))}
          </StyledArticleContainerDesktop>
        </div>
      )}
      {trendingPosts.length > showingNumber ? (
        <Button
          size="md"
          className="see-more-btn"
          onClick={() => setShowingNumber((v) => v + 4)}
        >
          {f({ id: 'experts.moreArticles', defaultMessage: 'More Articles' })}
        </Button>
      ) : null}
    </StyledContainer>
  )
}

export { ExpertRelativeArticleCard }
